import { Field } from '@today/ui-components'
import { Button, Label, Radio, RadioGroup, Textarea } from '@today/ui-primitives'
import { useId, useState } from 'react'
import type { RotateCertificateSubmit } from './provider-form.types'

/**
 * Props for {@link RotateCertificateForm}.
 *
 * @property providerName - The SAML provider being rotated, for the heading.
 * @property isBusy - True while a request is in flight; submit is disabled.
 * @property error - A refusal to announce (e.g. invalid metadata).
 * @property onSubmit - Reports exactly one new source.
 * @property onCancel - Closes the form.
 */
export interface RotateCertificateFormProps {
	providerName: string
	isBusy?: boolean
	error?: string | null
	onSubmit: (input: RotateCertificateSubmit) => void
	onCancel: () => void
}

enum RotationSource {
	URL = 'URL',
	XML = 'XML',
	PEM = 'PEM',
}

const submissionOf = (source: RotationSource, value: string): RotateCertificateSubmit => {
	if (source === RotationSource.URL) return { metadataUrl: value.trim() }
	if (source === RotationSource.XML) return { metadataXml: value }
	return { certificatePem: value.trim() }
}

/** The one input of the selected source. */
function SourceInput(props: { source: RotationSource; value: string; onChange: (value: string) => void }) {
	const id = useId()
	if (props.source === RotationSource.URL) {
		return <Field label="Metadata URL" value={props.value} onChange={(event) => props.onChange(event.target.value)} />
	}
	const label = props.source === RotationSource.XML ? 'Metadata XML' : 'Signing certificate (PEM)'
	return (
		<div className="flex flex-col gap-1.5">
			<Label htmlFor={id}>{label}</Label>
			<Textarea id={id} value={props.value} onChange={(event) => props.onChange(event.target.value)} />
		</div>
	)
}

/**
 * Rotate a SAML provider's signing certificate (TO-410 REQ-006) from a new
 * metadata URL, document or certificate. A rotation keeps the provider's
 * status: a verified provider stays live. Pure: the page's hook sends it.
 */
export function RotateCertificateForm({
	providerName,
	isBusy = false,
	error,
	onSubmit,
	onCancel,
}: RotateCertificateFormProps) {
	const [source, setSource] = useState(RotationSource.URL)
	const [value, setValue] = useState('')
	return (
		<section className="flex flex-col gap-4" aria-label={`Rotate certificate for ${providerName}`}>
			<h3 className="text-ds-base font-semibold text-ds-text">Rotate certificate · {providerName}</h3>
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
			<RadioGroup
				aria-label="New certificate source"
				value={source}
				onChange={(next) => {
					setSource(next as RotationSource)
					setValue('')
				}}
			>
				<Radio value={RotationSource.URL}>From a URL</Radio>
				<Radio value={RotationSource.XML}>From a document</Radio>
				<Radio value={RotationSource.PEM}>From a certificate</Radio>
			</RadioGroup>
			<SourceInput source={source} value={value} onChange={setValue} />
			<div className="flex gap-2">
				<Button variant="primary" isDisabled={isBusy} onPress={() => onSubmit(submissionOf(source, value))}>
					Rotate certificate
				</Button>
				<Button variant="secondary" isDisabled={isBusy} onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</section>
	)
}
